import React, { useEffect, useMemo, useState, useContext } from 'react';
import './ProductDetails.css';
import { LocaleContext, AuthContext, CartContext } from '../locale';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';

function parseIdFromHash() {
  const m = window.location.hash.match(/^#\/products\/([^/?#]+)/i);
  return m ? decodeURIComponent(m[1]) : '';
}

export default function ProductDetails() {
  const { locale } = useContext(LocaleContext);
  const { user, openAuth } = useContext(AuthContext);
  const { addToCart, isInCart } = useContext(CartContext);
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const id = parseIdFromHash();

  const nf = useMemo(
    () => new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: 'EUR' }),
    [locale]
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setErr('');
      try {
        // try direct endpoint
        let res = await fetch(`${API_BASE}/products/${id}`);
        if (!res.ok) {
          // fallback to list and find
          const resAll = await fetch(`${API_BASE}/products`);
          const dataAll = await resAll.json();
          const found = (dataAll.products || []).find(p => String(p.id) === String(id));
          if (!found) throw new Error('Produit introuvable');
          if (active) setProduct(found);
        } else {
          const data = await res.json();
          if (active) setProduct(data.product || data);
        }
      } catch (e) {
        if (active) setErr(e.message || 'Erreur');
      } finally {
        if (active) setLoading(false);
      }
    };
    if (id) load();

    const onHash = () => window.location.hash.startsWith('#/products/') && window.location.reload();
    window.addEventListener('hashchange', onHash);
    return () => { active = false; window.removeEventListener('hashchange', onHash); };
  }, [id]);

  const inc = () => setQty(q => {
    const max = Math.max(1, Math.min(99, Number(product?.stock_quantity) || 99));
    return Math.min(max, q + 1);
  });
  const dec = () => setQty(q => Math.max(1, q - 1));

  const handleAdd = () => {
    if (!user) {
      openAuth && openAuth('login');
      return;
    }
    if (Number(product?.stock_quantity) <= 0) return;

    // Best effort: pass qty and also loop to enforce the desired quantity if the cart ignores the qty arg.
    addToCart({ ...product, quantity: qty }, qty);
    for (let i = 1; i < qty; i++) {
      addToCart(product);
    }
  };

  if (!id) {
    return (
      <section className="product-details">
        <div className="container">
          <div className="pd-empty">
            <p>Produit introuvable.</p>
            <a className="btn secondary" href="#/products">Retour aux produits</a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="product-details">
      <div className="container">
        {loading ? (
          <div className="pd-loading">
            <div className="spinner" />
            Chargement du produit...
          </div>
        ) : err ? (
          <div className="pd-error">
            <div className="alert error">{err}</div>
            <a className="btn secondary" href="#/products">Retour aux produits</a>
          </div>
        ) : !product ? (
          <div className="pd-empty">
            <p>Produit introuvable.</p>
            <a className="btn secondary" href="#/products">Retour aux produits</a>
          </div>
        ) : (
          <div className="pd-layout">
            <div className={`pd-media ${product.category}`}>
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <div className="pd-fallback">{product.category === 'beer' ? '🍺' : '🍷'}</div>
              )}
            </div>

            <div className="pd-body">
              <button className="back-link" onClick={() => (window.location.hash = '#/products')}>
                ← Retour
              </button>

              <h1 className="pd-title">{product.name}</h1>
              <div className="pd-meta">
                <span>{product.marque}</span>
                {product.type ? <><span className="dot">•</span><span>{product.type}</span></> : null}
                {product.volume ? <><span className="dot">•</span><span>{product.volume}</span></> : null}
                {product.abv ? <><span className="dot">•</span><span>{product.abv}% vol</span></> : null}
              </div>

              <div className="pd-desc">{product.description || ''}</div>

              <div className="pd-price">{nf.format(product.price)}</div>
              <div className="pd-stock">
                {Number(product.stock_quantity) > 0 ? (
                  <span className="stock ok">{product.stock_quantity} en stock</span>
                ) : (
                  <span className="stock out">Rupture de stock</span>
                )}
              </div>

              <div className="pd-actions">
                <div className="qty">
                  <button onClick={dec} aria-label="Diminuer">−</button>
                  <div className="qty-value">{qty}</div>
                  <button onClick={inc} aria-label="Augmenter">+</button>
                </div>

                <button
                  className={`btn primary add ${Number(product?.stock_quantity) <= 0 ? 'in-cart' : ''}`}
                  onClick={handleAdd}
                  disabled={Number(product?.stock_quantity) <= 0}
                  title={Number(product?.stock_quantity) <= 0 ? 'Rupture de stock' : 'Ajouter au panier'}
                >
                  {Number(product?.stock_quantity) <= 0 ? 'Rupture de stock' : `Ajouter au panier`}
                </button>
              </div>

              <div className="pd-subtotal">
                <span>Sous-total</span>
                <strong>{nf.format(Number(product.price) * qty)}</strong>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
